import {
  z_ChatV8Capabilities,
  z_ChatV8Event,
  z_ChatV8ThreadPage,
  type T_ChatV8Event,
  type T_ChatV8ThreadPage,
  type T_ChatV8WidgetArtifactSnapshotResponse,
} from "@lattice/core-lib/types/chat-v8";
import type { T_ChatV8ClientContext, T_ChatV8ClientEvent } from "./machine";
import type {
  T_ChatV8ClientTransport,
  T_ChatV8CoordinatorTransport,
  T_ChatV8Subscription,
} from "./transport";
import {
  createChatV8WidgetSnapshotObserver,
  type T_ChatV8WidgetSnapshotObserverState,
} from "./widget-snapshot-observer";
import type { EventObject, MetaObject, SetupTypes } from "xstate";
import { assign, fromCallback, fromPromise, raise, setup } from "xstate";

const THREAD_EVENT_LIMIT = 100;
// Soft target: the repository may return more so a page starts at a user
// question instead of cutting into the model response that follows it.
const THREAD_PAGE_LIMIT = 30;
const RECONNECT_BASE_DELAY_MS = 350;
const RECONNECT_MAX_DELAY_MS = 2_500;

export type T_ChatV8CoordinatorObservation =
  | { kind: "capabilities_load_failed"; error: unknown }
  | { kind: "subscription_event_received"; event: T_ChatV8Event }
  | { kind: "subscription_event_rejected" }
  | { kind: "subscription_failed"; error: unknown }
  | {
      afterSeq: number;
      kind: "subscription_started";
      timelineVersion: number;
    }
  | {
      delayMs: number;
      kind: "subscription_reconnect_scheduled";
      reason: string;
    }
  | { kind: "thread_load_completed"; page: T_ChatV8ThreadPage }
  | { kind: "thread_load_failed"; error: unknown }
  | { kind: "thread_load_started" };

type T_ChatV8CoordinatorInput = {
  dispatchClient: (event: T_ChatV8ClientEvent) => void;
  formatError: (error: unknown) => string;
  getClientState: () => T_ChatV8ClientContext;
  initialThreadCuid: string | null;
  onObservation?: (observation: T_ChatV8CoordinatorObservation) => void;
  onThreadMissing: (threadCuid: string) => void;
  refreshThreadSelector: () => void;
  subscribeToClient: (listener: () => void) => () => void;
  subscribeToAppForeground: (listener: () => void) => () => void;
  subscribeToNetworkRecovery: (listener: () => void) => () => void;
  trackTerminalRunEvent: (
    event: T_ChatV8Event,
    events: T_ChatV8Event[],
  ) => void;
  transport: T_ChatV8CoordinatorTransport;
};

type T_SubscriptionTarget = {
  threadCuid: string;
  timelineVersion: number;
};

type T_ChatV8CoordinatorContext = {
  input: T_ChatV8CoordinatorInput;
  loadOlderPending: boolean;
  requestedThreadCuid: string | null;
  subscriptionTarget: T_SubscriptionTarget | null;
  widgetSnapshotsByArtifactCuid: Map<
    string,
    T_ChatV8WidgetArtifactSnapshotResponse
  >;
  widgetTimelineKey: string | null;
};

type T_ChatV8CoordinatorEvent =
  | { type: "LOAD_OLDER" }
  | { type: "REFRESH_THREAD" }
  | { type: "ROUTE_CHANGED"; threadCuid: string | null }
  | { type: "THREAD_MISSING" }
  | { type: "SUBSCRIPTION_TIMELINE_CHANGED" }
  | {
      state: T_ChatV8WidgetSnapshotObserverState;
      type: "WIDGET_SNAPSHOT_CACHE_CHANGED";
    };

type T_LoadThreadInput = {
  threadCuid: string;
  transport: Pick<T_ChatV8ClientTransport, "getThreadTail">;
};

type T_LoadOlderInput = {
  beforeDisplaySeq: number;
  threadCuid: string;
  timelineVersion: number;
  transport: Pick<T_ChatV8ClientTransport, "getThreadPage">;
};

type T_SubscriptionInput = {
  dispatchClient: (event: T_ChatV8ClientEvent) => void;
  formatError: (error: unknown) => string;
  getClientState: () => T_ChatV8ClientContext;
  onObservation:
    | ((observation: T_ChatV8CoordinatorObservation) => void)
    | undefined;
  refreshThreadSelector: () => void;
  subscribeToAppForeground: (listener: () => void) => () => void;
  subscribeToNetworkRecovery: (listener: () => void) => () => void;
  target: T_SubscriptionTarget;
  trackTerminalRunEvent: (
    event: T_ChatV8Event,
    events: T_ChatV8Event[],
  ) => void;
  transport: Pick<T_ChatV8ClientTransport, "getEvents" | "streamEvents">;
};

type T_WidgetSnapshotObserverInput = {
  getClientState: () => T_ChatV8ClientContext;
  subscribeToClient: (listener: () => void) => () => void;
  transport: Pick<T_ChatV8ClientTransport, "getWidgetArtifactSnapshot">;
};

function observe(
  input: Pick<T_ChatV8CoordinatorInput, "onObservation">,
  observation: T_ChatV8CoordinatorObservation,
): void {
  input.onObservation?.(observation);
}

const loadCapabilitiesActor = fromPromise(
  async ({ input }: { input: T_ChatV8CoordinatorInput }) => {
    const result = await input.transport.getCapabilities();
    return z_ChatV8Capabilities.parse(result);
  },
);

const loadThreadActor = fromPromise<T_ChatV8ThreadPage, T_LoadThreadInput>(
  async ({ input }) => {
    const result = await input.transport.getThreadTail({
      limit: THREAD_PAGE_LIMIT,
      threadCuid: input.threadCuid,
    });
    return z_ChatV8ThreadPage.parse(result);
  },
);

const loadOlderActor = fromPromise<T_ChatV8ThreadPage, T_LoadOlderInput>(
  async ({ input }) => {
    const result = await input.transport.getThreadPage({
      beforeDisplaySeq: input.beforeDisplaySeq,
      limit: THREAD_PAGE_LIMIT,
      threadCuid: input.threadCuid,
      timelineVersion: input.timelineVersion,
    });
    return z_ChatV8ThreadPage.parse(result);
  },
);

const maintainSubscriptionActor = fromCallback<
  EventObject,
  T_SubscriptionInput
>(({ input, sendBack }) => {
  let disposed = false;
  let pendingStreamEvents: T_ChatV8Event[] = [];
  let reconnectAttempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let streamFlushTimer: ReturnType<typeof setTimeout> | null = null;
  let subscription: T_ChatV8Subscription | null = null;
  let subscriptionGeneration = 0;

  const clearReconnectTimer = () => {
    if (reconnectTimer === null) return;
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  };

  const closeSubscription = () => {
    subscription?.unsubscribe();
    subscription = null;
  };

  const flushPendingStreamEvents = () => {
    streamFlushTimer = null;
    if (disposed || pendingStreamEvents.length === 0) return;

    const events = pendingStreamEvents;
    pendingStreamEvents = [];
    input.dispatchClient({ type: "EVENTS_RECEIVED", events });
  };

  const enqueueStreamEvent = (event: T_ChatV8Event) => {
    pendingStreamEvents.push(event);
    if (streamFlushTimer !== null) return;
    streamFlushTimer = setTimeout(flushPendingStreamEvents, 0);
  };

  const scheduleReconnect = (reason: string) => {
    if (disposed || reconnectTimer !== null) return;
    const delayMs = getReconnectDelayMs(reconnectAttempt);
    reconnectAttempt += 1;
    observe(input, {
      delayMs,
      kind: "subscription_reconnect_scheduled",
      reason,
    });
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void startSubscription();
    }, delayMs);
  };

  const syncMissedEvents = async () => {
    const current = input.getClientState();
    if (
      current.threadCuid !== input.target.threadCuid ||
      current.timelineVersion !== input.target.timelineVersion
    ) {
      return;
    }

    const result = await input.transport.getEvents({
      afterSeq: current.latestSeq,
      limit: THREAD_EVENT_LIMIT,
      threadCuid: input.target.threadCuid,
      timelineVersion: input.target.timelineVersion,
    });
    const events = parseEventArray(result.events);
    if (events === null) {
      observe(input, { kind: "subscription_event_rejected" });
      input.dispatchClient({
        type: "SUBSCRIPTION_ERROR",
        error: "Invalid Chat V8 event catch-up payload.",
      });
      return;
    }

    const analyticsEvents = [...current.events, ...events];
    for (const event of events) {
      input.trackTerminalRunEvent(event, analyticsEvents);
    }
    input.dispatchClient({
      type: "EVENTS_RECEIVED",
      events,
      latestSeq: result.latestSeq,
    });
  };

  async function startSubscription() {
    if (disposed) return;
    const generation = subscriptionGeneration + 1;
    subscriptionGeneration = generation;
    closeSubscription();
    const isCurrentSubscription = () =>
      !disposed && generation === subscriptionGeneration;

    try {
      await syncMissedEvents();
    } catch (error) {
      if (disposed) return;
      if (isTimelineChangedError(error)) {
        sendBack({ type: "SUBSCRIPTION_TIMELINE_CHANGED" });
        return;
      }
      if (isThreadNotFoundError(error)) {
        sendBack({ type: "THREAD_MISSING" });
        return;
      }
      input.dispatchClient({
        type: "SUBSCRIPTION_ERROR",
        error: input.formatError(error),
      });
    }

    if (!isCurrentSubscription()) return;
    subscription = input.transport.streamEvents(
      {
        afterSeq: input.getClientState().latestSeq,
        threadCuid: input.target.threadCuid,
        timelineVersion: input.target.timelineVersion,
      },
      {
        onComplete: () => {
          if (isCurrentSubscription()) scheduleReconnect("complete");
        },
        onData: (event) => {
          if (!isCurrentSubscription()) return;
          reconnectAttempt = 0;
          const parsed = z_ChatV8Event.safeParse(event);
          if (!parsed.success) {
            input.dispatchClient({
              type: "SUBSCRIPTION_ERROR",
              error: "Invalid Chat V8 stream event.",
            });
            observe(input, { kind: "subscription_event_rejected" });
            return;
          }

          observe(input, {
            event: parsed.data,
            kind: "subscription_event_received",
          });
          enqueueStreamEvent(parsed.data);
          input.trackTerminalRunEvent(parsed.data, [
            ...input.getClientState().events,
            ...pendingStreamEvents,
          ]);
          if (shouldRefreshThreadAfterEvent(parsed.data)) {
            input.refreshThreadSelector();
            sendBack({ type: "REFRESH_THREAD" });
          }
        },
        onError: (error) => {
          if (!isCurrentSubscription()) return;
          if (isTimelineChangedError(error)) {
            sendBack({ type: "SUBSCRIPTION_TIMELINE_CHANGED" });
            return;
          }
          if (isThreadNotFoundError(error)) {
            sendBack({ type: "THREAD_MISSING" });
            return;
          }
          input.dispatchClient({
            type: "SUBSCRIPTION_ERROR",
            error: input.formatError(error),
          });
          observe(input, { error, kind: "subscription_failed" });
          scheduleReconnect("error");
        },
        onStarted: () => {
          if (!isCurrentSubscription()) return;
          reconnectAttempt = 0;
          input.dispatchClient({
            type: "SUBSCRIPTION_STARTED",
            startedAt: new Date().toISOString(),
          });
          const current = input.getClientState();
          observe(input, {
            afterSeq: current.latestSeq,
            kind: "subscription_started",
            timelineVersion: input.target.timelineVersion,
          });
        },
        onStopped: () => {
          if (isCurrentSubscription()) scheduleReconnect("stopped");
        },
      },
    );
  }

  const revalidateConnection = (_reason: string) => {
    if (disposed) return;
    clearReconnectTimer();
    reconnectAttempt = 0;
    void startSubscription();
  };

  const removeAppStateListener = input.subscribeToAppForeground(() =>
    revalidateConnection("app_foregrounded"),
  );
  const removeNetworkListener = input.subscribeToNetworkRecovery(() =>
    revalidateConnection("network_recovered"),
  );
  void startSubscription();

  return () => {
    disposed = true;
    removeAppStateListener();
    removeNetworkListener();
    clearReconnectTimer();
    if (streamFlushTimer !== null) clearTimeout(streamFlushTimer);
    closeSubscription();
  };
});

const observeWidgetSnapshotsActor = fromCallback<
  EventObject,
  T_WidgetSnapshotObserverInput
>(({ input, sendBack }) => {
  const observer = createChatV8WidgetSnapshotObserver({
    loadSnapshot: input.transport.getWidgetArtifactSnapshot,
    onChange: (state) => {
      sendBack({ state, type: "WIDGET_SNAPSHOT_CACHE_CHANGED" });
    },
  });
  const inspectClientState = () => {
    void observer.inspect(input.getClientState());
  };
  const unsubscribe = input.subscribeToClient(inspectClientState);
  inspectClientState();

  return () => {
    unsubscribe();
    observer.dispose();
  };
});

const coordinatorSetupTypes: SetupTypes<
  T_ChatV8CoordinatorContext,
  T_ChatV8CoordinatorEvent,
  Record<never, string>,
  string,
  T_ChatV8CoordinatorInput,
  unknown,
  EventObject,
  MetaObject
> = {};

export const chatV8CoordinatorMachine = setup({
  types: coordinatorSetupTypes,
  actors: {
    loadCapabilities: loadCapabilitiesActor,
    loadOlder: loadOlderActor,
    loadThread: loadThreadActor,
    maintainSubscription: maintainSubscriptionActor,
    observeWidgetSnapshots: observeWidgetSnapshotsActor,
  },
  actions: {
    clearMissingThread: ({ context }) => {
      const threadCuid = context.requestedThreadCuid;
      context.input.dispatchClient({ type: "RESET_THREAD" });
      context.input.refreshThreadSelector();
      if (threadCuid !== null) context.input.onThreadMissing(threadCuid);
    },
    finishLoadOlder: assign({ loadOlderPending: false }),
    startLoadOlder: assign({ loadOlderPending: true }),
    refreshThreadSelector: ({ context }) =>
      context.input.refreshThreadSelector(),
    resetClient: ({ context }) =>
      context.input.dispatchClient({ type: "RESET_THREAD" }),
    routeChanged: assign(({ event }) => {
      if (event.type !== "ROUTE_CHANGED") return {};
      return {
        requestedThreadCuid: event.threadCuid,
        subscriptionTarget: null,
      };
    }),
    startThreadLoad: ({ context }) => {
      if (context.requestedThreadCuid === null) return;
      observe(context.input, { kind: "thread_load_started" });
      context.input.dispatchClient({
        type: "THREAD_LOAD_STARTED",
        threadCuid: context.requestedThreadCuid,
      });
    },
    applyWidgetSnapshotCache: assign(({ event }) => {
      if (event.type !== "WIDGET_SNAPSHOT_CACHE_CHANGED") return {};
      return {
        widgetSnapshotsByArtifactCuid: new Map(event.state.snapshots),
        widgetTimelineKey: event.state.key,
      };
    }),
  },
  guards: {
    canLoadOlder: ({ context }) => {
      const state = context.input.getClientState();
      return (
        state.threadCuid !== null &&
        state.timelineVersion !== null &&
        state.oldestDisplaySeq !== null
      );
    },
    hasRequestedThread: ({ context }) => context.requestedThreadCuid !== null,
    routeCleared: ({ event }) =>
      event.type === "ROUTE_CHANGED" && event.threadCuid === null,
    routeMatchesLoadedThread: ({ context, event }) =>
      event.type === "ROUTE_CHANGED" &&
      event.threadCuid !== null &&
      event.threadCuid === context.subscriptionTarget?.threadCuid,
  },
}).createMachine({
  id: "chatV8Coordinator",
  context: ({ input }) => ({
    input,
    loadOlderPending: false,
    requestedThreadCuid: input.initialThreadCuid,
    subscriptionTarget: null,
    widgetSnapshotsByArtifactCuid: new Map(),
    widgetTimelineKey: null,
  }),
  invoke: [
    {
      src: "loadCapabilities",
      input: ({ context }) => context.input,
      onDone: {
        actions: ({ context, event }) =>
          context.input.dispatchClient({
            type: "CAPABILITIES_LOADED",
            capabilities: event.output,
          }),
      },
      onError: {
        actions: ({ context, event }) => {
          observe(context.input, {
            error: event.error,
            kind: "capabilities_load_failed",
          });
        },
      },
    },
    {
      src: "observeWidgetSnapshots",
      input: ({ context }) => ({
        getClientState: context.input.getClientState,
        subscribeToClient: context.input.subscribeToClient,
        transport: context.input.transport,
      }),
    },
  ],
  on: {
    THREAD_MISSING: {
      actions: "clearMissingThread",
      target: ".connection.idle",
    },
    WIDGET_SNAPSHOT_CACHE_CHANGED: { actions: "applyWidgetSnapshotCache" },
  },
  type: "parallel",
  states: {
    connection: {
      initial: "deciding",
      states: {
        deciding: {
          always: [
            { guard: "hasRequestedThread", target: "loading" },
            { target: "idle" },
          ],
        },
        idle: {
          entry: ["resetClient", "refreshThreadSelector"],
          on: {
            ROUTE_CHANGED: [
              { actions: "routeChanged", guard: "routeCleared" },
              { actions: "routeChanged", target: "loading" },
            ],
          },
        },
        loading: {
          entry: "startThreadLoad",
          invoke: {
            src: "loadThread",
            input: ({ context }) => getLoadThreadInput(context),
            onDone: {
              actions: assign(({ context, event }) => {
                observe(context.input, {
                  kind: "thread_load_completed",
                  page: event.output,
                });
                context.input.dispatchClient({
                  type: "THREAD_LOAD_OK",
                  page: event.output,
                });
                context.input.refreshThreadSelector();
                return {
                  subscriptionTarget: {
                    threadCuid: event.output.threadCuid,
                    timelineVersion: event.output.timelineVersion,
                  },
                };
              }),
              target: "connected",
            },
            onError: [
              {
                actions: [
                  ({ context, event }) =>
                    observe(context.input, {
                      error: event.error,
                      kind: "thread_load_failed",
                    }),
                  "clearMissingThread",
                ],
                guard: ({ event }) => isThreadNotFoundError(event.error),
                target: "idle",
              },
              {
                actions: ({ context, event }) => {
                  observe(context.input, {
                    error: event.error,
                    kind: "thread_load_failed",
                  });
                  context.input.refreshThreadSelector();
                  context.input.dispatchClient({
                    type: "THREAD_LOAD_ERROR",
                    error: context.input.formatError(event.error),
                  });
                },
                target: "failed",
              },
            ],
          },
          on: {
            ROUTE_CHANGED: [
              {
                actions: "routeChanged",
                guard: "routeCleared",
                target: "idle",
              },
              { actions: "routeChanged", reenter: true, target: "loading" },
            ],
          },
        },
        connected: {
          invoke: {
            src: "maintainSubscription",
            input: ({ context }) => getSubscriptionInput(context),
          },
          on: {
            REFRESH_THREAD: { target: "loading" },
            ROUTE_CHANGED: [
              {
                actions: "routeChanged",
                guard: "routeCleared",
                target: "idle",
              },
              { guard: "routeMatchesLoadedThread" },
              { actions: "routeChanged", target: "loading" },
            ],
            SUBSCRIPTION_TIMELINE_CHANGED: { target: "loading" },
          },
        },
        failed: {
          on: {
            REFRESH_THREAD: { target: "loading" },
            ROUTE_CHANGED: [
              {
                actions: "routeChanged",
                guard: "routeCleared",
                target: "idle",
              },
              { actions: "routeChanged", target: "loading" },
            ],
          },
        },
      },
    },
    pagination: {
      initial: "idle",
      states: {
        idle: {
          on: {
            LOAD_OLDER: {
              actions: "startLoadOlder",
              guard: "canLoadOlder",
              target: "loading",
            },
          },
        },
        loading: {
          invoke: {
            src: "loadOlder",
            input: ({ context }) => getLoadOlderInput(context),
            onDone: {
              actions: [
                ({ context, event }) =>
                  context.input.dispatchClient({
                    type: "LOAD_OLDER_OK",
                    page: event.output,
                  }),
                "finishLoadOlder",
              ],
              target: "idle",
            },
            onError: [
              {
                actions: ["finishLoadOlder", raise({ type: "REFRESH_THREAD" })],
                guard: ({ event }) => isTimelineChangedError(event.error),
                target: "idle",
              },
              {
                actions: [
                  ({ context, event }) =>
                    context.input.dispatchClient({
                      type: "MUTATION_ERROR",
                      error: context.input.formatError(event.error),
                    }),
                  "finishLoadOlder",
                ],
                target: "idle",
              },
            ],
          },
        },
      },
    },
  },
});

function getLoadThreadInput(
  context: T_ChatV8CoordinatorContext,
): T_LoadThreadInput {
  if (context.requestedThreadCuid === null) {
    throw new Error("Chat V8 thread load started without a thread");
  }
  return {
    threadCuid: context.requestedThreadCuid,
    transport: context.input.transport,
  };
}

function getLoadOlderInput(
  context: T_ChatV8CoordinatorContext,
): T_LoadOlderInput {
  const state = context.input.getClientState();
  if (
    state.threadCuid === null ||
    state.timelineVersion === null ||
    state.oldestDisplaySeq === null
  ) {
    throw new Error("Chat V8 pagination started without a loaded page");
  }
  return {
    beforeDisplaySeq: state.oldestDisplaySeq,
    threadCuid: state.threadCuid,
    timelineVersion: state.timelineVersion,
    transport: context.input.transport,
  };
}

function getSubscriptionInput(
  context: T_ChatV8CoordinatorContext,
): T_SubscriptionInput {
  if (context.subscriptionTarget === null) {
    throw new Error("Chat V8 subscription started without a loaded thread");
  }
  return {
    dispatchClient: context.input.dispatchClient,
    formatError: context.input.formatError,
    getClientState: context.input.getClientState,
    onObservation: context.input.onObservation,
    refreshThreadSelector: context.input.refreshThreadSelector,
    subscribeToAppForeground: context.input.subscribeToAppForeground,
    subscribeToNetworkRecovery: context.input.subscribeToNetworkRecovery,
    target: context.subscriptionTarget,
    trackTerminalRunEvent: context.input.trackTerminalRunEvent,
    transport: context.input.transport,
  };
}

function getReconnectDelayMs(attempt: number): number {
  return Math.min(
    RECONNECT_MAX_DELAY_MS,
    RECONNECT_BASE_DELAY_MS * Math.max(1, attempt + 1),
  );
}

function shouldRefreshThreadAfterEvent(event: T_ChatV8Event): boolean {
  return (
    event.type === "run.completed" ||
    event.type === "run.failed" ||
    event.type === "run.cancelled" ||
    event.type === "run.waiting_for_input" ||
    event.type === "elicitation.answered"
  );
}

export function isTimelineChangedError(error: unknown): boolean {
  return getErrorMessage(error).includes("THREAD_TIMELINE_CHANGED");
}

export function isThreadNotFoundError(error: unknown): boolean {
  const message = getErrorMessage(error);
  return (
    message.includes("Thread not found") ||
    message.includes("NOT_FOUND") ||
    message.includes("No record was found")
  );
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function parseEventArray(events: T_ChatV8Event[]): T_ChatV8Event[] | null {
  const parsed = z_ChatV8Event.array().safeParse(events);
  return parsed.success ? parsed.data : null;
}
